import React, { useState, useEffect } from 'react';
import {
  MessageSquare,
  Brain,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Loader2,
  Target,
  Award,
  RefreshCw,
  Mic,
  Play,
  ArrowRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { TechCard, TechButton, TechBadge } from './TechUI';
import { careerAPI } from '../utils/api';
import { toast } from 'react-hot-toast';
import UpgradePrompt from './UpgradePrompt';

const InterviewPrepModule = ({ userId, roadmaps: propRoadmaps }) => {
  const navigate = useNavigate();
  const [selectedCareerPath, setSelectedCareerPath] = useState('');
  const [roadmaps, setRoadmaps] = useState(propRoadmaps || []);
  const [loadingRoadmaps, setLoadingRoadmaps] = useState(!propRoadmaps);
  const [difficulty, setDifficulty] = useState('medium');
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [userAnswers, setUserAnswers] = useState({});
  const [showAnswers, setShowAnswers] = useState({});
  const [feedback, setFeedback] = useState({});
  const [loadingFeedback, setLoadingFeedback] = useState({});
  const [limitError, setLimitError] = useState(null);
  const [limitErrorFeedback, setLimitErrorFeedback] = useState(null);

  useEffect(() => {
    // Fetch roadmaps from API if not provided via props
    if (!propRoadmaps) {
      fetchRoadmaps();
    } else {
      setRoadmaps(propRoadmaps);
      if (propRoadmaps.length > 0) {
        setSelectedCareerPath(propRoadmaps[0].careerPath);
      }
    }
  }, [propRoadmaps]);

  const fetchRoadmaps = async () => {
    try {
      setLoadingRoadmaps(true);
      const response = await careerAPI.getUserRoadmaps();
      const userRoadmaps = response.roadmaps || [];
      setRoadmaps(userRoadmaps);
      if (userRoadmaps.length > 0) {
        setSelectedCareerPath(userRoadmaps[0].careerPath);
      }
    } catch (error) {
      console.error('Error loading roadmaps:', error);
    } finally {
      setLoadingRoadmaps(false);
    }
  };

  const handleGenerateQuestions = async () => {
    if (!selectedCareerPath) {
      toast.error('Please select a roadmap first');
      return;
    }

    setLoading(true);
    try {
      const response = await careerAPI.generateInterviewQuestions(selectedCareerPath, difficulty);
      setQuestions(response.questions || response);
      setUserAnswers({});
      setShowAnswers({});
      setExpandedQuestions({});
      setLimitError(null);
      toast.success(`Generated ${response.questions?.length || response.length} interview questions!`);
    } catch (error) {
      console.error('Error generating questions:', error);
      if (error.isLimitError && error.limitInfo) {
        setLimitError(error.limitInfo);
      } else {
        toast.error(error.message || 'Failed to generate interview questions');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleQuestion = (index) => {
    setExpandedQuestions(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const toggleShowAnswer = (index) => {
    setShowAnswers(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleAnswerChange = (index, value) => {
    setUserAnswers(prev => ({
      ...prev,
      [index]: value
    }));
  };

  const handleGetFeedback = async (index) => {
    const question = questions[index];
    const userAnswer = userAnswers[index];

    if (!userAnswer?.trim()) {
      toast.error('Please enter an answer first');
      return;
    }

    setLoadingFeedback(prev => ({ ...prev, [index]: true }));

    try {
      const sampleAnswer = question.sample_answer ||
        question.expected_answer_points?.join(' ') ||
        'No sample answer available';

      const response = await careerAPI.evaluateInterviewAnswer(
        question.question,
        userAnswer,
        sampleAnswer
      );

      setFeedback(prev => ({
        ...prev,
        [index]: response
      }));

      setLimitErrorFeedback(null);
      toast.success('Feedback generated!');
    } catch (error) {
      console.error('Error getting feedback:', error);
      if (error.isLimitError && error.limitInfo) {
        setLimitErrorFeedback(error.limitInfo);
      } else {
        toast.error(error.message || 'Failed to get AI feedback');
      }
    } finally {
      setLoadingFeedback(prev => ({ ...prev, [index]: false }));
    }
  };

  const getTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'technical':
        return 'text-blue-400 bg-blue-900/30 border-blue-700';
      case 'behavioral':
        return 'text-purple-400 bg-purple-900/30 border-purple-700';
      case 'system design':
        return 'text-orange-400 bg-orange-900/30 border-orange-700';
      case 'coding':
        return 'text-green-400 bg-green-900/30 border-green-700';
      default:
        return 'text-slate-400 bg-slate-900/30 border-slate-700';
    }
  };

  const getDifficultyBadge = (diff) => {
    switch (diff?.toLowerCase()) {
      case 'easy':
        return <TechBadge type="success">Easy</TechBadge>;
      case 'medium':
        return <TechBadge type="warning">Medium</TechBadge>;
      case 'hard':
        return <TechBadge type="error">Hard</TechBadge>;
      default:
        return <TechBadge>{diff}</TechBadge>;
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader2 className="w-16 h-16 text-emerald-500 animate-spin mb-4" />
        <p className="text-xl text-white font-semibold">Generating interview questions...</p>
        <p className="text-slate-400 mt-2">AI is preparing role-specific questions for you</p>
      </div>
    );
  }

  if (loadingRoadmaps) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mb-4" />
        <p className="text-lg text-white">Loading your roadmaps...</p>
      </div>
    );
  }

  // Show CTA if no roadmaps exist
  if (roadmaps.length === 0) {
    return (
      <TechCard className="p-12 text-center">
        <Target className="w-20 h-20 text-slate-600 mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-white mb-2">No Roadmaps Yet</h3>
        <p className="text-slate-400 max-w-md mx-auto mb-6">
          Create a career roadmap first to unlock personalized interview questions tailored to your career goals.
        </p>
        <TechButton
          onClick={() => navigate('/free-results')}
          icon={ArrowRight}
          className="text-lg px-8"
        >
          Create Your First Roadmap
        </TechButton>
      </TechCard>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header Section */}
      <TechCard className="p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-3xl font-bold text-white mb-2">Interview Preparation AI</h2>
            <p className="text-slate-400">
              Practice role-specific questions with AI-powered evaluation and feedback
            </p>
          </div>
          <MessageSquare className="w-12 h-12 text-emerald-400" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <label className="block text-white font-semibold mb-2">Select From Your Roadmaps</label>
            <select
              value={selectedCareerPath}
              onChange={(e) => setSelectedCareerPath(e.target.value)}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="">-- Choose a roadmap --</option>
              {roadmaps.map((roadmap, index) => (
                <option key={roadmap.id || index} value={roadmap.careerPath}>
                  {roadmap.careerPath}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-white font-semibold mb-2">Difficulty</label>
            <select
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value)}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </div>

          <div className="flex items-end">
            <TechButton
              onClick={handleGenerateQuestions}
              disabled={!selectedCareerPath || loading}
              icon={RefreshCw}
              className="w-full text-lg py-3"
            >
              Generate
            </TechButton>
          </div>
        </div>
      </TechCard>

      {/* Limit Error - Show Upgrade Prompt */}
      {limitError && (
        <UpgradePrompt
          title="Interview Question Limit Reached"
          message={limitError.message || "You've reached your interview question generation limit. Upgrade to continue generating questions."}
          feature="Interview Questions"
          limit={limitError.limit}
          used={limitError.used}
        />
      )}

      {limitErrorFeedback && (
        <UpgradePrompt
          title="Interview Evaluation Limit Reached"
          message={limitErrorFeedback.message || "You've reached your interview evaluation limit. Upgrade to continue getting AI feedback."}
          feature="Interview Evaluations"
          limit={limitErrorFeedback.limit}
          used={limitErrorFeedback.used}
        />
      )}

      {/* Statistics Cards */}
      {questions.length > 0 && !limitError && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <TechCard className="p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-400 text-sm">Total Questions</span>
              <Target className="w-5 h-5 text-slate-400" />
            </div>
            <p className="text-3xl font-bold text-white">{questions.length}</p>
          </TechCard>

          <TechCard className="p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-400 text-sm">Technical</span>
              <Brain className="w-5 h-5 text-blue-400" />
            </div>
            <p className="text-3xl font-bold text-white">
              {questions.filter(q => q.type?.toLowerCase() === 'technical').length}
            </p>
          </TechCard>

          <TechCard className="p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-400 text-sm">Behavioral</span>
              <MessageSquare className="w-5 h-5 text-purple-400" />
            </div>
            <p className="text-3xl font-bold text-white">
              {questions.filter(q => q.type?.toLowerCase() === 'behavioral').length}
            </p>
          </TechCard>

          <TechCard className="p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-400 text-sm">Answered</span>
              <CheckCircle className="w-5 h-5 text-emerald-400" />
            </div>
            <p className="text-3xl font-bold text-white">
              {Object.keys(userAnswers).filter(key => userAnswers[key]?.trim()).length}
            </p>
          </TechCard>
        </div>
      )}

      {/* Questions List */}
      {questions.length > 0 && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-2xl font-bold text-white">Interview Questions</h3>
            <div className="flex gap-2">
              {getDifficultyBadge(difficulty)}
            </div>
          </div>

          {questions.map((question, index) => (
            <TechCard key={index} className="p-6 hover:border-emerald-500/50 transition-all">
              <div
                className="cursor-pointer"
                onClick={() => toggleQuestion(index)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-grow">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="bg-emerald-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">
                        {index + 1}
                      </div>
                      <div className={`px-3 py-1 rounded-lg border ${getTypeColor(question.type)}`}>
                        <span className="text-sm font-bold">{question.type}</span>
                      </div>
                    </div>
                    <p className="text-xl text-white font-semibold leading-relaxed ml-11">
                      {question.question}
                    </p>
                  </div>
                  {expandedQuestions[index] ? (
                    <ChevronUp className="w-6 h-6 text-slate-400 ml-4 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-6 h-6 text-slate-400 ml-4 flex-shrink-0" />
                  )}
                </div>
              </div>

              {/* Expanded Content */}
              {expandedQuestions[index] && (
                <div className="mt-6 space-y-6 border-t border-slate-700 pt-6 ml-11">
                  {/* Answer Input */}
                  <div>
                    <label className="block text-emerald-400 font-semibold mb-3 flex items-center">
                      <Mic className="w-5 h-5 mr-2" />
                      Your Answer
                    </label>
                    <textarea
                      value={userAnswers[index] || ''}
                      onChange={(e) => handleAnswerChange(index, e.target.value)}
                      placeholder="Type your answer here... (or click the mic icon for voice recording - coming soon)"
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 min-h-[120px] resize-y"
                    />
                    <div className="flex justify-between items-center mt-3">
                      <span className="text-slate-500 text-sm">
                        {userAnswers[index]?.length || 0} characters
                      </span>
                      <div className="flex gap-2">
                        <TechButton
                          onClick={() => toast.success('Voice recording coming soon!')}
                          icon={Mic}
                          className="bg-slate-700 hover:bg-slate-600 text-sm py-2"
                        >
                          Record Audio
                        </TechButton>
                        <TechButton
                          onClick={() => handleGetFeedback(index)}
                          icon={Brain}
                          className="text-sm py-2"
                          disabled={!userAnswers[index]?.trim() || loadingFeedback[index]}
                        >
                          {loadingFeedback[index] ? 'Evaluating...' : 'Get AI Feedback'}
                        </TechButton>
                      </div>
                    </div>
                  </div>

                  {/* Expected Answer Points */}
                  {question.expected_answer_points && (
                    <div>
                      <button
                        onClick={() => toggleShowAnswer(index)}
                        className="flex items-center space-x-2 text-yellow-400 hover:text-yellow-300 font-semibold mb-3 transition-colors"
                      >
                        <AlertCircle className="w-5 h-5" />
                        <span>{showAnswers[index] ? 'Hide' : 'Show'} Key Points to Cover</span>
                      </button>

                      {showAnswers[index] && (
                        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                          <ul className="space-y-3">
                            {question.expected_answer_points.map((point, i) => (
                              <li key={i} className="flex items-start space-x-3">
                                <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                                <span className="text-white leading-relaxed">{point}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* AI Feedback */}
                  {feedback[index] && (
                    <div className="mt-6 border-t border-slate-700 pt-6">
                      <h4 className="text-lg font-semibold text-emerald-400 mb-4 flex items-center">
                        <Brain className="w-5 h-5 mr-2" />
                        AI Feedback
                      </h4>

                      {/* Score */}
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-slate-400 text-sm">Overall Score</span>
                          <span className={`text-2xl font-bold ${feedback[index].score >= 80 ? 'text-emerald-400' :
                            feedback[index].score >= 60 ? 'text-yellow-400' : 'text-red-400'
                            }`}>
                            {feedback[index].score}/100
                          </span>
                        </div>
                        <div className="w-full bg-slate-800 rounded-full h-2">
                          <div
                            className={`h-full rounded-full ${feedback[index].score >= 80 ? 'bg-emerald-500' :
                              feedback[index].score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                            style={{ width: `${feedback[index].score}%` }}
                          />
                        </div>
                      </div>

                      {/* Strengths */}
                      {feedback[index].strengths && feedback[index].strengths.length > 0 && (
                        <div className="mb-4">
                          <h5 className="text-emerald-400 font-semibold mb-2 flex items-center">
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Strengths
                          </h5>
                          <ul className="space-y-1">
                            {feedback[index].strengths.map((strength, i) => (
                              <li key={i} className="text-white text-sm flex items-start">
                                <span className="text-emerald-400 mr-2">✓</span>
                                {strength}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Weaknesses */}
                      {feedback[index].weaknesses && feedback[index].weaknesses.length > 0 && (
                        <div className="mb-4">
                          <h5 className="text-yellow-400 font-semibold mb-2 flex items-center">
                            <AlertCircle className="w-4 h-4 mr-2" />
                            Areas for Improvement
                          </h5>
                          <ul className="space-y-1">
                            {feedback[index].weaknesses.map((weakness, i) => (
                              <li key={i} className="text-white text-sm flex items-start">
                                <span className="text-yellow-400 mr-2">•</span>
                                {weakness}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Suggestions */}
                      {feedback[index].suggestions && feedback[index].suggestions.length > 0 && (
                        <div className="mb-4">
                          <h5 className="text-blue-400 font-semibold mb-2 flex items-center">
                            <Target className="w-4 h-4 mr-2" />
                            Suggestions
                          </h5>
                          <ul className="space-y-1">
                            {feedback[index].suggestions.map((suggestion, i) => (
                              <li key={i} className="text-white text-sm flex items-start">
                                <span className="text-blue-400 mr-2">→</span>
                                {suggestion}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Overall Feedback */}
                      {feedback[index].overall_feedback && (
                        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                          <p className="text-white leading-relaxed">{feedback[index].overall_feedback}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Tips for this question type */}
                  {question.tips && (
                    <div>
                      <h4 className="text-lg font-semibold text-emerald-400 mb-3 flex items-center">
                        <Award className="w-5 h-5 mr-2" />
                        Tips for {question.type} Questions
                      </h4>
                      <div className="bg-emerald-900/20 border border-emerald-700/50 rounded-xl p-4">
                        <p className="text-white leading-relaxed">{question.tips}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </TechCard>
          ))}
        </div>
      )}

      {/* Mock Interview Section */}
      {questions.length > 0 && (
        <TechCard className="p-8 bg-gradient-to-br from-emerald-900/20 to-teal-900/20 border-emerald-700/50">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold text-white mb-2">Ready for a Mock Interview?</h3>
              <p className="text-slate-300">
                Practice with our AI interviewer in real-time and get instant feedback on your performance
              </p>
            </div>
            <TechButton
              onClick={() => toast.success('Mock interview feature coming soon!')}
              icon={Play}
              className="text-lg px-8 py-4"
            >
              Start Mock Interview
            </TechButton>
          </div>
        </TechCard>
      )}

      {/* Empty State */}
      {questions.length === 0 && !loading && (
        <TechCard className="p-12 text-center">
          <MessageSquare className="w-20 h-20 text-slate-600 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-white mb-2">No Questions Generated Yet</h3>
          <p className="text-slate-400 max-w-md mx-auto">
            Select your target career path, choose a difficulty level, and click "Generate" to receive AI-powered interview questions with key talking points.
          </p>
        </TechCard>
      )}
    </div>
  );
};

export default InterviewPrepModule;

